import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Hidden,
  Drawer,
  Divider,
  Icon,
  Menu as MenuUI,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Menu, AccountCircle } from '@material-ui/icons';
import PropTypes from 'prop-types';

import VLIPlus from '~/assets/images/logo.png';
import { Spinner, Navigation } from '~/components';
import { t } from '~/helpers';
import { signOut } from '~/store/modules/auth/actions';
import {
  requestNavigation,
  toggleDrawer,
} from '~/store/modules/drawer/actions';

import { ChangePassword } from './components';
import { Container, NavBar, Navi, Logo, Main, Avatar } from './styles';

const useStyles = makeStyles(() => ({
  drawerPaper: {
    width: '280px',
    marginTop: 0,
    justifyContent: 'flex-start',
  },
}));

function DefaultLayout({ children }) {
  const { innerWidth } = window;
  const { title, navigations, loading, opened } = useSelector(
    (state) => state.drawer
  );

  const { translate } = useSelector((state) => state.language);
  const { profile } = useSelector((state) => state.user);

  const classes = useStyles();
  const dispatch = useDispatch();

  const [width, setWidth] = useState(innerWidth);
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState(null);
  const [password, setPassword] = useState(false);

  const websiteJSON = JSON.parse(sessionStorage.getItem('web'));
  const website = useMemo(
    () => ({
      logo: VLIPlus,
      site_name: websiteJSON ? websiteJSON.site_name : 'VLI+ Unidata',
    }),
    [websiteJSON]
  );

  /**
   * Toggle drawer
   */
  async function handleToggleDrawer() {
    await dispatch(toggleDrawer(!opened));
  }

  async function handleFormPassword() {
    setPassword(true);
    setActions(null);
  }

  useEffect(() => {
    setWidth(innerWidth);
  }, [innerWidth]);

  useEffect(() => {
    const defNavigation = navigations.map((navi) => {
      if (navi.submenus.length === 0) {
        return {
          key: navi.idmenu,
          title: t(navi.codigolabelsistema),
          url: navi.rotamenu,
          icon: <Icon>{navi.iconemenu || 'label'}</Icon>,
          mobile: navi.moduloAPP.toUpperCase() === 'N',
        };
      }

      return {
        key: navi.idmenu,
        title: t(navi.codigolabelsistema),
        icon: <Icon>{navi.iconemenu || 'label'}</Icon>,
        mobile: navi.moduloAPP.toUpperCase() === 'N',
        children: navi.submenus.map((one) => {
          if (one.submenus.length === 0) {
            return {
              key: one.idmenu,
              title: t(one.codigolabelsistema),
              url: one.rotamenu,
              mobile: one.moduloAPP.toUpperCase() === 'N',
            };
          }

          return {
            key: one.idmenu,
            title: t(one.codigolabelsistema),
            mobile: one.moduloAPP.toUpperCase() === 'N',
            children: one.submenus.map((two) => ({
              key: two.idmenu,
              title: t(two.codigolabelsistema),
              url: two.rotamenu,
              mobile: two.moduloAPP.toUpperCase() === 'N',
            })),
          };
        }),
      };
    });

    setItems(defNavigation);
  }, [navigations, translate]);

  useEffect(() => {
    dispatch(requestNavigation());
  }, [dispatch]);

  return (
    <Container open={opened}>
      <CssBaseline />
      <AppBar position="fixed">
        <Toolbar>
          <IconButton color="inherit" onClick={() => handleToggleDrawer()}>
            <Menu />
          </IconButton>

          <Typography variant="h6" noWrap style={{ width: '100%' }}>
            {title}
          </Typography>

          <IconButton
            color="inherit"
            edge="end"
            onClick={(e) => setActions(e.currentTarget)}
          >
            <Avatar>
              <AccountCircle />
              <p>
                {profile.name}
                <small>{profile.nomeperfil}</small>
              </p>
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <NavBar open={opened}>
        <Spinner loading={loading} />

        <Hidden smUp implementation="css">
          {width <= 768 && (
            <Drawer
              variant="temporary"
              anchor="left"
              open={opened}
              onClose={() => dispatch(toggleDrawer(false))}
              classes={{
                paper: classes.drawerPaper,
                margin: 0,
              }}
              ModalProps={{ keepMounted: true }}
            >
              <Navi open={opened}>
                <Logo open={opened}>
                  <img
                    src={
                      websiteJSON && websiteJSON.logo_mobile
                        ? websiteJSON.logo_mobile
                        : website.logo
                    }
                    alt={
                      websiteJSON && websiteJSON.site_name
                        ? websiteJSON.site_name
                        : website.site_name
                    }
                  />
                </Logo>
                <Divider />
                <Navigation lists={items} />
              </Navi>
            </Drawer>
          )}
        </Hidden>

        <Hidden xsDown implementation="css">
          {width > 768 && (
            <Drawer
              variant="permanent"
              anchor="left"
              className="drawer-desktop"
            >
              <Navi open={opened}>
                <Logo open={opened}>
                  <img
                    src={
                      websiteJSON && websiteJSON.logo
                        ? websiteJSON.logo
                        : website.logo
                    }
                    alt={
                      websiteJSON && websiteJSON.site_name
                        ? websiteJSON.site_name
                        : website.site_name
                    }
                  />
                </Logo>
                <Divider />
                <Navigation lists={items} />
              </Navi>
            </Drawer>
          )}
        </Hidden>
      </NavBar>

      <Main open={opened}>{children}</Main>

      <MenuUI
        anchorEl={actions}
        keepMounted
        open={!!actions}
        onClose={() => setActions(null)}
      >
        <MenuItem onClick={() => handleFormPassword()}>Alterar Senha</MenuItem>
        <MenuItem onClick={() => dispatch(signOut())}>Sair</MenuItem>
      </MenuUI>

      <ChangePassword password={password} close={() => setPassword(false)} />
    </Container>
  );
}

DefaultLayout.propTypes = {
  children: PropTypes.element.isRequired,
};

export default DefaultLayout;
